/**
 * 主要是围绕 messageList 提供的方法函数
 *
 * 主要概念：
 * messageId / id - 前端生成的消息 id
 * idFromServer - 后端生成的消息 id
 */
import dayjs from 'dayjs'
// import uuid from 'uuid/v4'
import get from 'lodash.get'
import set from 'lodash.set'
import has from 'lodash.has'
import timer from '@/utils/timer'
import formatMessageForRender from '@/utils/message_render'
import {
    MESSAGE_NAME,
    INVALID_TIME_MESSAGE,
    STATUS,
    MESSAGE_FROM,
    HOST,
    CHAT_TARGET
} from '@/constants/message'
import { isSameDate, findLastIndex } from '@/utils'
import { imgSrcLoaded } from '@/utils/dom'
import { scrollTo } from '@/utils/dom'
import sensitiveCache from '@/utils/sensitive_cache'
// import scrollIntoView from 'scroll-into-view-if-needed'


const TWO_MINUTES = 2 * 60 * 1000

function getMessageSwiperName (mode, content) {
    switch (mode) {
        case 'text': return MESSAGE_NAME.TEXT
        case 'recommend': return getMessageOptionName(content.mode)
        case 'small': return MESSAGE_NAME.SWIPER_ICON
        case 'full': // 和下方一致
        case 'full_recommends': return MESSAGE_NAME.SWIPER_IMG_TEXT
        case 'general': return MESSAGE_NAME.SWIPER_LIST
    }
}

function getMessageOptionName (mode) {
    switch (mode) {
        // 推荐问题 统一组件
        case 'normal':
        case 'options':
        case 'no_answer': return MESSAGE_NAME.OPTION_NORMAL
    }
}
export default {
    methods: {
        /**
         * 校验 index 是否在消息列表范围
         */
        validMessageIndex (index, messageList) {
            if (index < 0 || index >= this.getMessageLength(messageList)) {
                throw new Error(
                    'index 超出 MessageList 范围:',
                    index,
                    this.getMessageLength(messageList)
                )
            }
            return true
        },
        /**
         * 获取消息列表长度
         */
        getMessageLength (messageList) {
            return (messageList || this.messageList).length
        },
        /**
         * 根据 id 获取消息的 index
         */
        getMessageIndexById (id, messageList) {
            return findLastIndex(
                (messageList || this.messageList),
                item => item.baseProps && item.baseProps.id === id
            )
        },
        getMessageById (id) {
            const matchIndex = this.getMessageIndexById(id)
            return this.getMessageByIndex(matchIndex)
        },
        /**
         * 根据 index 获取消息
         */
        getMessageByIndex (index, messageList) {
            this.validMessageIndex(index, messageList)
            return (messageList || this.messageList)[index]
        },
        /**
         * 根据 id 获取消息的 path 值
         * path 支持 a.b.c
         */
        getMessageValueById (id, path) {
            const matchIndex = this.getMessageIndexById(id)
            if (matchIndex !== -1) {
                return get(this.messageList[matchIndex], path)
            }
        },
        /**
         * 根据 index 获取消息的 path 值
         * path 支持 a.b.c
         */
        getMessageValueByIndex (index, path) {
            this.validMessageIndex(index)
            return get(this.messageList[index], path)
        },
        getMessageIndexByIdFromServer (idFromServer, messageList) {
            return findLastIndex(
                (messageList || this.messageList),
                message => message.server && message.server.id === idFromServer
            )
        },
        /**
         * 根据 id 设置消息的 path 值
         * path 支持 a.b.c
         */
        setMessageValueById (id, path, value) {
            const matchIndex = this.getMessageIndexById(id)
            if (matchIndex !== -1) {
                this.setMessageValueByIndex(matchIndex, path, value)
            }
        },
        /**
         * 根据 index 设置消息的 path 值
         * path 支持 a.b.c
         */
        setMessageValueByIndex (index, path, value) {
            this.validMessageIndex(index)
            const messageItem = this.messageList[index]
            if (!has(messageItem, path)) {
                const keys = path.split('.')
                if (keys.length === 1) {
                    this.$set(messageItem, path, value)
                } else {
                    let target = messageItem
                    keys.forEach((key, index) => {
                        if (index === keys.length - 1) {
                            this.$set(target, key, value)
                        } else {
                            this.$set(target, key, Object.assign({}, target[key]))
                            target = target[key]
                        }
                    })
                }
            } else {
                set(messageItem, path, value)
            }
        },
        /**
         * 根据 idFromServer 设置消息的 path 值
         */
        setMessageValueByIdFromServer (idFromServer, path, value) {
            const matchIndex = this.getMessageIndexByIdFromServer(idFromServer)
            if (matchIndex !== -1) {
                this.setMessageValueByIndex(matchIndex, path, value)
            }
        },
        /**
         * 根据 id 删除消息
         */
        removeMessageById (id, messageList) {
            const matchIndex = this.getMessageIndexById(id, messageList)
            if (matchIndex !== -1) {
                (messageList || this.messageList).splice(matchIndex, 1)
            }
        },
        /**
         * 向消息列表尾部插入消息
         * @param { Object } data
         * @param { String } data.name
         * @param { String } data.host
         * @param { Object } data.content
         * @param { Object } [data.server]
         * @param { Boolean } [data.isUnread]
         * @param { Number } [data.timeout]
         * @param { Boolean } [forceInsertTime = false] - 若为 true，则绕过 detectInsertTime 插入时间消息
         */
        appendMessage (data, forceInsertTime) {
            const message = formatMessageForRender(data)
            const messageId = message.baseProps.id
            const messageTimeStamp = message.baseProps.timestamp
            const needInsertTime = forceInsertTime || this.detectInsertTime(messageTimeStamp)
            if (needInsertTime) {
                this.appendMessageTime(messageTimeStamp)
            }
            this.messageList.push(message)

            this.scrollToMessage(message) // 1、先滚动到新增消息位置

            this.$nextTick(() => { // 2、再判断是机器人消息（定位至访客最新提问消息）
                const lastMsg = this.getLastMessage()
                if (this.chatTarget === CHAT_TARGET.ROBOT) { // 与机器人聊天
                    if (lastMsg.hasOwnProperty('server') && lastMsg.server.id && lastMsg.server.from === MESSAGE_FROM.ROBOT) { // 排除欢迎语等伪机器人消息
                        const newUserMsg = this.getRobotBeforeMessage()
                        if (newUserMsg) {
                            this.scrollToMessage(newUserMsg)
                        } else {
                            this.scrollToMessage(message)
                        }
                    }
                } else { // 正常情况，定位至最新消息
                    this.scrollToMessage(message)
                }
            })

            /**
             * 若有 timeout 参数，则该消息会定时删掉
             */
            if (typeof data.timeout === 'number') {
                this.delayRemoveMessage(messageId, data.timeout)
            }

            return message
        },
        /**
         * 在该 baseMessageId 所指的 Message 后面插入消息
         * @param { Object } data
         * @param { String } data.name
         * @param { String } data.host
         * @param { Object } data.content
         * @param { Object } [data.server]
         * @param { Boolean } [data.isUnread]
         * @param { Number } [data.timeout]
         * @param { String } baseMessageId
         */
        insertMessage (data, baseMessageId) {
            const message = formatMessageForRender(data)
            const messageId = message.baseProps.id

            this.insertMessageAfterId(baseMessageId, message)

            this.scrollToMessage(message)

            /**
             * 若有 timeout 参数，则该消息会定时删掉
             */
            if (typeof data.timeout === 'number') {
                this.delayRemoveMessage(messageId, data.timeout)
            }

            return message
        },
        /**
         * 视口滚到特定消息
         * 如果图片则会等到加载完成后再滚
         */
        scrollToMessage (message) {
            const id = message.baseProps.id
            const name = message.name
            const host = message.baseProps.host || ''
            this.$nextTick(async () => {
                try {
                    const $messageDom = document.getElementById(id)
                    if (!$messageDom) {
                        return
                    }
                    if (name === MESSAGE_NAME.IMAGE && host === HOST.USER) { // 访客发出的图片消息
                        await imgSrcLoaded(message.content.url)
                    }
                    setTimeout(() => {
                        // scrollIntoView($messageDom, {
                        //     scrollMode: 'if-needed'
                        // })
                        try {
                            $messageDom.scrollIntoViewIfNeeded()
                        } catch (err) {
                            $messageDom.scrollIntoView()
                        }

                    }, 200)
                } catch (err) {
                    // eslint-disable-next-line
                    console.error(err)
                    this.scrollToBottom()
                }
            })
        },
        /**
         * 视图滚动到底
         */
        scrollToBottom () {
            this.$nextTick(() => {
                setTimeout(() => {
                    scrollTo(this.$refs.messageList.$el, 0, 'scrollHeight')
                }, 200)
            })
        },
        /**
         * 获取 机器人返回消息之前-访客发出的消息
         */
        getRobotBeforeMessage () {
            let list = this.messageList.filter(item => {
                const isHost = item.baseProps.hasOwnProperty('host')
                const isUser = item.baseProps.host === HOST.USER
                return isHost && isUser // 筛选出所有访客消息
            })
            if (list.length === 0) {
                return null
            }
            return list[list.length - 1]
        },

        /**
         * 根据 id 延迟删除消息
         */
        delayRemoveMessage (messageId, timeout) {
            const timerId = timer.add(timeout, () => {
                this.removeMessageById(messageId)
                timer.remove(timerId)
            })
            return timerId
        },
        /**
         * 在指定位置插入消息片段（数据）
         * @param { Number } startIndex
         * @param { Array } messageList
         */
        insertMessageSegmentByIndex (startIndex, messageList) {
            this.messageList.splice(startIndex, 0, ...messageList)
        },
        insertMessageSegmentById (messageId, messageList) {
            const index = this.getMessageIndexById(messageId)

            const startIndex = index === -1
                ? this.getMessageLength()
                : index + 1

            this.insertMessageSegmentByIndex(startIndex, messageList)
        },
        /* eslint-disable */
        insertMessageBeforeId (id, message) {

        },
        /* eslint-enable */

        insertMessageAfterId (messageId, message) {
            this.insertMessageSegmentById(messageId, [message])
        },

        /**
         * 用新消息替换指定位置的旧消息（应用场景如消息撤回）
         * 需要注意消息的哪些字段需要保留！
         */
        replaceMessageByIndex (index, message) {
            this.validMessageIndex(index)
            this.messageList.splice(index, 1, message)
        },
        /**
         * 向消息列表插入消息时，判断是否需要先插入时间消息
         * 符合以下条件则插入时间消息：
         *  1. 消息列表为空数组
         *  2. 没有作为比较的先前消息
         *  3. 与先前消息相差时间大于指定时间（2分钟）
         * @param { Number } timestamp - ms
         * @param { String } [baseId] - 以哪个 messageId 为参考，若不传则认为消息列表的最后一个
         * @param { String } [key=baseProps] - baseProps || server，即取前端还是后端的时间戳，
         * 前者用于实时聊天，后者用于历史记录
         * @param { Array } messageList
         * @returns { Boolean }
         */
        detectInsertTime (timestamp, baseId, key = 'baseProps', messageList) {
            const messageLength = this.getMessageLength(messageList)

            // 1
            if (messageLength === 0) {
                return true
            }

            let baseMessageIndex = baseId
                ? this.getMessageIndexById(baseId, messageList)
                : messageLength - 1


            // 2
            const lastValidMessage = findLastValidMessage.call(this, baseMessageIndex, messageList)
            if (!lastValidMessage) {
                return true
            }
            const lastValidMessageTimestamp = lastValidMessage[key].timestamp
            // 3
            if (timestamp - lastValidMessageTimestamp > TWO_MINUTES) {
                return true
            }

            return false

            function findLastValidMessage (baseMessageIndex, messageList) {
                while (baseMessageIndex) {
                    const curMessage = this.getMessageByIndex(baseMessageIndex, messageList)
                    if (!INVALID_TIME_MESSAGE.includes(curMessage.name)) {
                        return curMessage
                    }
                    baseMessageIndex -= 1
                }
            }
        },
        /**
         * 生成时间消息
         */
        generateMessageTime (timestamp) {
            const format = isSameDate(timestamp) ? 'HH:mm' : 'YYYY-MM-DD HH:mm'
            return formatMessageForRender({
                name: MESSAGE_NAME.TIME,
                content: dayjs(timestamp).format(format)
            })
        },
        /**
         * 生成 socket 连接消息
         */
        generateMessageSocket (text, status) {
            return formatMessageForRender({
                name: MESSAGE_NAME.EVENT_SOCKET,
                content: {
                    text,
                    status
                }
            })
        },
        /**
         * 向消息列表插入时间消息
         */
        appendMessageTime (timestamp) {
            this.messageList.push(this.generateMessageTime(timestamp))
        },
        /**
         * 向消息列表插入 socket 连接消息
         */
        appendMessageSocketConnecting () {
            const message = this.generateMessageSocket(
                '正在连接，请稍后',
                STATUS.SENDING
            )
            this.socketEventMessageId = message.baseProps.id
            this.messageList.push(message)
            this.scrollToMessage(message)
        },
        /**
         * 向消息列表插入欢迎语文本消息
         */
        appendWelcomeText (welcomeText, type, guideTime) {
            let v = this
            if (type === 'welcome') {
                welcomeText.forEach((val, index) => {
                    let showtime = true
                    if (index !== 0) {
                        showtime = false
                    }
                    let newtime = setTimeout(()=>{
                        v.appendMessage({
                            name: MESSAGE_NAME.TEXT,
                            host: HOST.SERVICE,
                            content: val.text,
                            server: {
                                from: MESSAGE_FROM.ROBOT
                            }
                        }, showtime)
                        this.$eventBus.$emit('showinputstatus', {is_input:false})
                        clearTimeout(newtime)
                    }, val.duration * 1000)
                })
            } else if (type === 'welcomeGuide') {
                let newguide = setTimeout(()=>{
                    v.appendMessage({
                        name: MESSAGE_NAME.TEXT,
                        host: HOST.SERVICE,
                        content: welcomeText,
                        server: {
                            from: MESSAGE_FROM.ROBOT
                        }
                    }, true)
                    this.$eventBus.$emit('showinputstatus', {is_input:false})
                    clearTimeout(newguide)
                }, guideTime * 1000) 
            }
        },
        /**
         * 向消息列表插入套电消息
         */
        appendTask (taxkText) {
            let v = this
            console.log(taxkText)
            taxkText.answer.forEach((val) => {
                v.appendMessage({
                    name: getMessageSwiperName(val.detail_type, val.data),
                    host: HOST.SERVICE,
                    content: val.data,
                    server: {
                        from: MESSAGE_FROM.ROBOT,
                        id: taxkText.id,
                        timestamp: taxkText.timestamp
                    }
                }, false)
            })
            this.$eventBus.$emit('showinputstatus', {is_input:false})
        },
        appendWelcomeSwiperList (mode, messageData, forceInsertTime = false, guideTime) {
            let newlist = setTimeout(()=>{
                this.appendMessage({
                    name: getMessageSwiperName(mode),
                    host: HOST.SERVICE,
                    content: messageData,
                    server: {
                        from: MESSAGE_FROM.ROBOT
                    }
                }, forceInsertTime)
                this.$eventBus.$emit('showinputstatus', {is_input:false})
                clearTimeout(newlist)
            }, guideTime * 1000)
        },

        withdrawMessage (name, { msg_id: idFromServer }) {
            const matchIndex = this.getMessageIndexByIdFromServer(idFromServer)
            if (matchIndex === -1) {
                return
            }
            this.replaceMessageByIndex(matchIndex, formatMessageForRender({
                name,
                timestamp: this.getMessageValueByIndex(matchIndex, 'baseProps.timestamp'),
                content: '对方撤回了一条消息'
            }))
        },

        // historys和tell中，未读消息标示为已读
        unReadHandler ({msg_ids}) {
            msg_ids.map((val)=> {
                this.noreadmsgid.add(val)
            })
            this.messageList.forEach(item => {
                if (item.server) {
                    if (msg_ids.includes(item.server.id)) {
                        item.baseProps.isUnread = true
                        this.noreadmsgid.delete(item.server.id)
                    }
                }
            })
        },
        updateSensitiveMessageContent (name, { msg_id: idFromServer, data: sensitiveData }) {
            const { text: newMessageContentText } = sensitiveData

            const matchIndex = this.getMessageIndexByIdFromServer(idFromServer)

            if (matchIndex === -1) {
                sensitiveCache.add(idFromServer, newMessageContentText)
            } else {
                this.setMessageValueByIndex(matchIndex, 'content.text', newMessageContentText)
            }
        },

        // 退出排队（事件消息）仅最后一个可点
        setTheLastEventQueueId (messageList) {
            const theLastEventQueueIndex = findLastIndex(
                messageList,
                message => message.name === MESSAGE_NAME.EVENT_QUEUE
            )

            this.theLastEventQueueId = theLastEventQueueIndex === -1
                ? ''
                : this.getMessageValueByIndex(theLastEventQueueIndex, 'baseProps.id')
        },
        
        // 获取最后一条消息
        getLastMessage () {
            const messageListLength = this.messageList.length
            if (messageListLength === 0) {
                return null
            }
            return this.messageList[messageListLength - 1]
        }
    }
}
